import { useMemo } from "react";
import { useAppSelector, selectAuth, selectPreloadData } from "@redux";
import { linkImage } from "../Helpers";

type UserListType = {
  id: number | null | undefined;
  first_name: string;
  last_name: string;
  avatar?: { path?: string };
  type?: "user";
};

export function useSortedUserList(items: UserListType[], mapResults = true, userAtTop = true, page = "Schedule") {
  const auth = useAppSelector(selectAuth);
  const userId = auth?.userInfo?.id;
  const preloadData = useAppSelector(selectPreloadData);

  return useMemo(() => {
    // Early return if no items
    if (!items || items.length === 0) {
      return [];
    }

    try {
      const userDisplayOrder = preloadData?.displayOrder?.find(
        (item) => item.user_id === userId
      );

      let userListOrder: (number | string)[] = [];

      const sessionOrderRaw = sessionStorage.getItem(`userListOrder${page}`);
      if (sessionOrderRaw) {
        try {
          const sessionOrder = JSON.parse(sessionOrderRaw);
          if (Array.isArray(sessionOrder)) {
            userListOrder = sessionOrder;
          }
        } catch {
          console.warn("Invalid session userListOrder format");
        }
      }

      if (userListOrder.length === 0 && userDisplayOrder?.list_user_position) {
        try {
          const parsedData = JSON.parse(userDisplayOrder.list_user_position);
          if (
            parsedData &&
            typeof parsedData === "object" &&
            Array.isArray(parsedData.userOrder)
          ) {
            userListOrder = parsedData.userOrder;
          }
        } catch (parseError) {
          console.warn("Failed to parse preload user display order:", parseError);
        }
      }

      const users = [...items];
      const uniqueUsers = users.filter(
        (user, index, self) =>
          user.id === null ||
          user.id === undefined ||
          index === self.findIndex((u) => u.id === user.id)
      );

      const sortedUsers = uniqueUsers.sort((a, b) => {
        if (!a.id || !b.id) return 0;

        if (!Array.isArray(userListOrder) || userListOrder.length === 0) {
          if (userAtTop && a.id === userId) return -1;
          if (userAtTop && b.id === userId) return 1;
          return a.id - b.id;
        }

        if (userAtTop && a.id === userId) return -1;
        if (userAtTop && b.id === userId) return 1;

        const indexA = userListOrder.indexOf(a.id || 0);
        const indexB = userListOrder.indexOf(b.id || 0);

        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return 0;
      });

      const mappedUsers = sortedUsers.map((user) => ({
        label: `${user.last_name} ${user.first_name}`,
        value: user?.id?.toString(),
        avatar: linkImage(user?.avatar?.path),
        id: user?.id,
        first_name: user?.first_name,
        last_name: user?.last_name,
        type: "user",
      }));

      return mapResults ? [...mappedUsers] : [...sortedUsers];
    } catch (error) {
      console.error('Error in useSortUserList:', error);
      
      if (mapResults) {
        return items.map((user) => ({
          label: `${user.last_name} ${user.first_name}`,
          value: user?.id?.toString(),
          avatar: linkImage(user?.avatar?.path),
          id: user?.id,
          first_name: user?.first_name,
          last_name: user?.last_name,
          type: "user",
        }));
      }

      return [...items];
    }
  }, [items, mapResults, userId, preloadData]); // Dependencies
}

export function useSortedGroupList(items: any[], mapResults = true) {
  const auth = useAppSelector(selectAuth);
  const userId = auth?.userInfo?.id;
  const preloadData = useAppSelector(selectPreloadData);

  // Early return if no items
  if (!items || items.length === 0) {
    return [];
  }

  try {
    const userDisplayOrder = preloadData?.displayOrder?.find(
      (item) => item.user_id === userId
    );

    let groupListOrder: (number | string)[] = [];

    const sessionOrderRaw = sessionStorage.getItem("groupListOrder");
    if (sessionOrderRaw) {
      try {
        const sessionOrder = JSON.parse(sessionOrderRaw);
        if (Array.isArray(sessionOrder)) {
          groupListOrder = sessionOrder;
          //console.log('Session groupListOrder:', groupListOrder);
        }
      } catch {
        console.warn("Invalid session groupListOrder format");
      }
    }

    if (userDisplayOrder?.list_user_position) {
      try {
        const parsedData = JSON.parse(userDisplayOrder.list_user_position);
        if (
          parsedData &&
          typeof parsedData === "object" &&
          Array.isArray(parsedData.groupOrder)
        ) {
          //console.log('Preload group display order:', parsedData.groupOrder);
          const groupsInDatabaseNotInSession = parsedData.groupOrder.filter((groupId) => !groupListOrder.includes(groupId));
          groupListOrder = [...groupListOrder, ...groupsInDatabaseNotInSession];
          //console.log('Updated groupListOrder with preload data:', groupListOrder);
        }
      } catch (parseError) {
        console.warn("Failed to parse preload group display order:", parseError);
      }
    }

    const groups = [...items];
    const uniqueGroups = groups.filter(
      (group, index, self) =>
        group.id === null ||
        group.id === undefined ||
        index === self.findIndex((g) => g.id === group.id)
    );

    const sortedGroups = uniqueGroups.sort((a, b) => {
      if (!a.id || !b.id) return 0;

      // If there is no order for the group, sort based on id
      if (!Array.isArray(groupListOrder) || groupListOrder.length === 0) {
        return a.id - b.id;
      }

      // Safe indexOf calls with additional type checking
      const indexA = groupListOrder.indexOf(a.id || 0);
      const indexB = groupListOrder.indexOf(b.id || 0);

      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return 0;
    });

    const mappedGroups = sortedGroups.map((group) => ({
      label: group.name,
      value: group.id?.toString(),
      id: group.id,
      type: "group",
    }));

    //console.log('Sorted groups:', sortedGroups);
    return mapResults ? [...mappedGroups] : [...sortedGroups];
  } catch (error) {
    // If anything goes wrong, return the original items
    console.error('Error in useSortGroupList:', error);
    
    if (mapResults) {
      // Return mapped version of original items
      return items.map((group) => ({
        label: group.name,
        value: group.id?.toString(),
        id: group.id,
        type: "group",
      }));
    }
    
    return [...items];
  }
}